import { useCallback, useMemo } from 'react';
import type { GridColDef, GridColumnVisibilityModel } from '@mui/x-data-grid-premium';
import { useAuthStore } from '../stores/auth.store';
import { usersService } from '../services/users.service';
import { mergeColumnOrder } from '../utils/merge-column-order';

/** Structural, not metrics. Two of these have no header text to find them by again. */
const STRUCTURAL = new Set(['__detail_panel_toggle__', '_profitability', 'display_name']);
// A combination's _dim_* columns stand in for display_name; outside the catalogue, a saved order would push them last.
const isStructural = (f: string) => STRUCTURAL.has(f) || f.startsWith('_dim_');

const SLOT_KEYS = {
  report: { order: 'columns', hidden: 'hidden_columns' },
  clicks: { order: 'clicks_columns', hidden: 'clicks_hidden_columns' },
  conversions: { order: 'conversions_columns', hidden: 'conversions_hidden_columns' },
} as const;

export type ColumnSlot = keyof typeof SLOT_KEYS;

/**
 * Applies the user's column choice to a grid, and saves a new one. The stored value is
 * exactly what the columns dialog shows, so applying it is a lookup.
 */
export function useColumnPrefs(allColumns: GridColDef[], catalogue: string[], slot: ColumnSlot = 'report') {
  const settings = useAuthStore((s) => s.user?.settings);
  const setUserSettings = useAuthStore((s) => s.setUserSettings);
  const keys = SLOT_KEYS[slot];
  const tracking = settings?.tracking as Record<string, unknown> | undefined;
  const order = tracking?.[keys.order] as string[] | undefined;
  const hidden = tracking?.[keys.hidden] as string[] | undefined;

  const columns = useMemo(() => {
    if (!order?.length) return allColumns;
    // Same merge the dialog seeds from, so the grid and the list agree on where a
    // newly shipped column sits.
    const merged = mergeColumnOrder(order, catalogue);
    const rank = new Map(merged.map((f, i) => [f, i]));
    const at = (c: GridColDef) => rank.get(c.field) ?? Infinity;
    return [
      ...allColumns.filter((c) => isStructural(c.field)),
      ...allColumns.filter((c) => !isStructural(c.field)).sort((a, b) => at(a) - at(b)),
    ];
  }, [allColumns, order, catalogue]);

  const visibility = useMemo<GridColumnVisibilityModel>(() => {
    const model: GridColumnVisibilityModel = {};
    for (const f of hidden || []) if (!isStructural(f)) model[f] = false;
    return model;
  }, [hidden]);

  const save = useCallback(async (nextOrder: string[], nextHidden: string[]) => {
    // Read at call time, not captured at render, so a save builds on the current settings.
    const current = useAuthStore.getState().user?.settings || {};
    const id = useAuthStore.getState().user?.id;
    if (!id) return;

    const merged = {
      ...current,
      tracking: { ...current.tracking, [keys.order]: nextOrder, [keys.hidden]: nextHidden },
    };
    setUserSettings(merged);   // optimistic — the grid must not wait on the network
    try {
      await usersService.update(id, { settings: merged });
    } catch {
      setUserSettings(current);
    }
  }, [setUserSettings, keys.order, keys.hidden]);

  // Stable identities: the dialog seeds from these, and a fresh [] each render would
  // re-seed it mid-drag.
  const orderOut = useMemo(() => order || [], [order]);
  const hiddenOut = useMemo(() => hidden || [], [hidden]);

  return { columns, visibility, save, order: orderOut, hidden: hiddenOut };
}
